import { useMemo, useState } from 'react'
import { useUpdateUserStatusMutation, UserStatusEnum } from '@/@data'
import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { useNotification, useTranslator } from '@/hooks'
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  // Divider,
  TextField,
  Typography,
  Box,
  Avatar,
  Divider
} from '@mui/material'

import { Button } from '@/@core/components/mui/Button'

import { useForm, FormProvider, Controller } from 'react-hook-form'

interface IGuestWaitingNoteModal {
  open: boolean
  isPending?: boolean
  setOpen: (open: boolean) => void
  dataRequest: any
  onSuccess: () => void
}

export function BulkingNoteDisableDialog({ open, setOpen, dataRequest, onSuccess }: IGuestWaitingNoteModal) {
  const { __ } = useTranslator()
  const n = useNotification()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const users = useMemo(
    () => (Array.isArray(dataRequest) ? dataRequest : dataRequest ? [dataRequest] : []),
    [dataRequest]
  )

  const handleClose = () => {
    setOpen(false)
  }

  const methods = useForm({
    defaultValues: {
      notes: {}
    }
  })

  const { mutate: updateDisableStatus, isPending } = useUpdateUserStatusMutation()

  const handleDisable = async (data: any) => {
    if (!users.length) return

    try {
      setIsSubmitting(true)

      const notesRecord = (data && data.notes) || {}

      const ids: number[] = users.map((u: any) => u.id)

      const notes = users.map((u: any) => {
        const message = notesRecord?.[u.id]

        return message ? { message } : undefined
      })

      await new Promise<void>((resolve, reject) => {
        updateDisableStatus(
          {
            id: ids,
            status: UserStatusEnum.INACTIVE,
            note: notes as any
          },
          {
            onSuccess: () => resolve(),
            onError: () => reject()
          }
        )
      })

      n.success(__('User status updated successfully'))

      onSuccess()
      handleClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      scroll='body'
      fullWidth
      closeAfterTransition={false}
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '1270px' }
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleDisable)}>
          <DialogCloseButton onClick={handleClose} sx={{ position: 'absolute', right: 0, top: 0, zIndex: 1302 }}>
            <i className='tabler-x' />
          </DialogCloseButton>

          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 2, width: '100%' }}>
              <Typography variant='h6' component='span'>
                {__('Bulking Note ')}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {users.map((user: any) => (
                <Box key={user.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0, flex: 1 }}>
                    <Avatar
                      src={user?.avatar_file?.path_url || user?.avatar}
                      alt={user?.first_name}
                      sx={{ width: 56, height: 56 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Typography
                          variant='h6'
                          sx={{
                            mb: 0,
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            width: '170px'
                          }}
                        >
                          {user?.first_name}
                        </Typography>
                        <Button
                          variant='contained'
                          className='bg-[#6F83F6]'
                          sx={{ borderRadius: 10, height: '26px', fontSize: '12px' }}
                        >
                          {user.status}
                        </Button>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} className='text-[#2F2B3D66]'>
                        <i className='tabler-id' />
                        <span className='text-[14px] leading-[20px]'>{user?.system_code}</span>
                        <i
                          className='tabler-copy'
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            navigator.clipboard.writeText(user?.system_code || '')
                            n.success(__('Copied to clipboard!'))
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ borderRadius: 2, p: 0, flex: 2 }}>
                    <Controller
                      name={`notes.${user.id}`}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder={__('Note')}
                          multiline
                          minRows={2}
                          fullWidth
                          sx={{
                            width: '100%',
                            background: '#fff',
                            borderRadius: '10px',
                            '& textarea': {
                              fontSize: '16px',
                              color: '#A8AAAE'
                            }
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </DialogContent>

          <DialogActions className='px-[20px] sm:px-[24px]'>
            <Button
              variant='contained'
              onClick={handleClose}
              sx={{ borderRadius: 10, backgroundColor: '#6F83F629', height: 38, color: '#6F83F6' }}
            >
              {__('Cancel')}
            </Button>
            <Button
              type='submit'
              variant='contained'
              disabled={isPending || isSubmitting}
              endIcon={<i className='tabler-check' />}
              sx={{ borderRadius: 10, height: 38, backgroundColor: '#FF9900', color: '#fff' }}
            >
              {isPending ? (
                <>
                  <CircularProgress size={16} color='inherit' />
                  {__('Updating...')}
                </>
              ) : (
                __('Submit')
              )}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
